import { useState } from "react";
import { darkTheme as t } from "../theme";

export default function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await onLogin(username, password);
    } catch {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: t.bgDeep,
    border: "none",
    borderBottom: `1px solid ${t.borderGold}`,
    color: t.textPrimary,
    padding: "12px 4px",
    fontSize: 14,
    fontFamily: t.fontBody,
    fontWeight: t.weightBody,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    display: "block",
    fontFamily: t.fontBody,
    fontSize: 10,
    fontWeight: t.weightSub,
    color: t.textMuted,
    letterSpacing: 3,
    textTransform: "uppercase",
    marginBottom: 10,
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: t.bgDeep,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: t.fontBody,
        position: "relative",
        overflow: "hidden",
      }}>

        {/* Background gradients */}
        <div style={{
          position: "fixed", inset: 0, zIndex: 0,
          background: `
            radial-gradient(ellipse at 20% 50%, rgba(125,21,37,0.08) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 20%, rgba(201,168,76,0.05) 0%, transparent 50%),
            radial-gradient(ellipse at 60% 80%, rgba(125,21,37,0.04) 0%, transparent 40%)
          `,
        }} />

        {/* Grid */}
        <div style={{
          position: "fixed", inset: 0, zIndex: 0,
          backgroundImage: `
            linear-gradient(rgba(201,168,76,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201,168,76,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }} />

        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 420, padding: "0 24px" }}>

         {/* Logo + Header */}
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <img
             src="/logo-login.png"
             alt="Wayne Enterprises"
             style={{ height: 350, width: "auto", display: "block", margin: "0 auto 24px" }}
            />
            
            <p style={{
              fontFamily: t.fontBody,
              fontSize: 11,
              fontWeight: t.weightSub,
              color: t.textMuted,
              letterSpacing: 5,
              textTransform: "uppercase",
              margin: 0,
            }}>
              Private Client Portal
            </p>
          </div>

          {/* Card */}
          <div style={{
            background: t.surface,
            border: `1px solid ${t.borderGold}`,
            borderTop: `2px solid ${t.gold}`,
            padding: "40px 36px",
            boxShadow: `0 0 60px rgba(201,168,76,0.08), 0 0 120px rgba(125,21,37,0.04)`,
          }}>
            <form onSubmit={handleSubmit}>

              {/* Username */}
              <div style={{ marginBottom: 28 }}>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  style={inputStyle}
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: 36 }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    style={{ ...inputStyle, paddingRight: 52 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    style={{
                      position: "absolute", right: 4, top: "50%",
                      transform: "translateY(-50%)",
                      background: "none", border: "none",
                      color: t.textMuted, cursor: "pointer",
                      fontSize: 10, fontFamily: t.fontBody,
                      fontWeight: t.weightSub, letterSpacing: 1,
                      padding: "4px 6px",
                    }}
                  >
                    {showPass ? "HIDE" : "SHOW"}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{
                  background: "rgba(192,57,43,0.08)",
                  border: `1px solid ${t.rubyLight}`,
                  padding: "10px 14px",
                  marginBottom: 20,
                }}>
                  <p style={{ color: t.rubyLight, fontSize: 12, fontFamily: t.fontBody, margin: 0, letterSpacing: 1 }}>
                    {error}
                  </p>
                </div>
              )}

              {/* Submit */}
             <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  background: loading ? t.primarySub : t.primary,
                  color: "#F0E6D0",
                  border: `1px solid ${t.primarySub}`,
                  padding: "14px",
                  fontSize: 11,
                  fontFamily: t.fontBody,
                  fontWeight: t.weightDisplay,
                  letterSpacing: 4,
                  textTransform: "uppercase",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  boxShadow: loading ? "none" : `0 0 24px rgba(125,21,37,0.3), 0 4px 12px rgba(0,0,0,0.3)`,
                }}
              >
                {loading ? "Authenticating..." : "Sign In"}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p style={{
            textAlign: "center",
            color: t.textMuted,
            fontSize: 10,
            fontFamily: t.fontBody,
            letterSpacing: 2,
            marginTop: 32,
            textTransform: "uppercase",
          }}>
            Wayne Enterprises, LLC — Confidential
          </p>
        </div>
      </div>
    </>
  );
}